import React from "react";
import { images } from "~/assets";
import styles from "./InvoiceDetails.module.scss";
import classNames from "classnames/bind";
import dayjs from "dayjs";
import PrimaryButton from "~/components/common/primary-button/PrimaryButton";
import { LockIcon } from "lucide-react";

const cx = classNames.bind(styles);

interface ILineItem {
  name: string;
  quantity: number;
  price: number;
  amount: number;
}
interface ISectionsTable {
  title: string;
  lines: ILineItem[];
}

interface IInvoicePaymentDetails {
  paymentDate: string;
  reference: string;
  amount: string;
  method: string;
  status?: string;
}

export interface IInvoiceInInvoiceDetails {
  customer: {
    name: string;
    address: string;
    phone: string;
    email: string;
  };

  secondaryBillingAddress?: {
    name: string;
    address: string;
  };

  salesRepresentative: {
    name: string;
    email: string;
    phone: string;
  };

  insuranceCompany: {
    name?: string,
    number?: string,
    email?: string
  };

  invoiceTable: {
    invoice: string;
    date: string;
    amountDue: string;
    dueDate: string;
  };

  sectionsTable: ISectionsTable[];

  note: string;

  subTotalTable: {
    subTotal: string;
    tax: string;
    taxRate: number;
    total: string;
    amountPaid: string;
    balanceDue: string;
  };

  invoicePayments?: IInvoicePaymentDetails[];
}

interface IInvoiceDetailsProps {
  invoice: IInvoiceInInvoiceDetails | null;
  onMakePayment?: () => void;
}

const InvoiceDetails = (props: IInvoiceDetailsProps) => {
  const canPay = Number(props.invoice?.subTotalTable.balanceDue) > 0;
  const tableInformation = [
    { label: "Invoice", value: props.invoice?.invoiceTable.invoice || "-" },
    {
      label: "Date",
      value:
        `${dayjs(props.invoice?.invoiceTable.date).format("MM/DD/YYYY")}` ||
        "-",
    },
    {
      label: "Amount Due",
      value: `$${props.invoice?.invoiceTable.amountDue || "-"}`,
    },
    {
      label: "Due Date",
      value:
        `${dayjs(props.invoice?.invoiceTable.dueDate).format("MM/DD/YYYY")}` ||
        "-",
    },
  ];

  const subTotalTableInformation = [
    {
      label: "Sub Total",
      value: `$${props.invoice?.subTotalTable.subTotal || "-"}`,
    },
    {
      label: `Tax (${props.invoice?.subTotalTable.taxRate || 0}%)`,
      value: `$${props.invoice?.subTotalTable.tax || "0"}`,
    },
    { label: "Total", value: `$${props.invoice?.subTotalTable.total || "-"}` },
    {
      label: "Amount Paid",
      value: `$${props.invoice?.subTotalTable.amountPaid || "-"}`,
    },
    {
      label: "Balance Due",
      value: `$${props.invoice?.subTotalTable.balanceDue || "-"}`,
    },
  ];

  const invoicePayments = props.invoice?.invoicePayments || [];
  const hasInvoicePayments = invoicePayments.length > 0;

  const formatCurrency = (value: string) => {
    const numeric = Number(value);
    if (Number.isNaN(numeric)) {
      return value;
    }

    return numeric.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  return (
    <div className={cx("invoice-details-container")}>
      <div className={cx("header")}>
        <h3 className={cx("title")}>-- INVOICE --</h3>
      </div>

      <div className={cx("information-container")}>
        <div className={cx("information-row")}>
          <div className={cx("section-row")}>
            <div className={cx("item-row")}>
              <p className={cx("item-value")}>Christian Brothers Roofing</p>
              <p className={cx("item-value")}>
                11300 Decimal Drive,<br />
                Suite D <br />
                Louisville, KY 40299
              </p>
              <p className={cx("item-value")}>
                (502) 244-0208
              </p>
            </div>

            <div className={cx("item-row")}>
              <p className={cx("item-label")}>Sales Representative</p>
              <p className={cx("item-value")}>
                {props.invoice?.salesRepresentative.name}
              </p>
              <p className={cx("item-value")}>
                {props.invoice?.salesRepresentative.phone}
              </p>
              <p className={cx("item-value")}>
                {props.invoice?.salesRepresentative.email}
              </p>
            </div>
          </div>

          <img src={images.logo} alt="Logo" className={cx("logo-image")} />
        </div>

        <div className={cx("information-row", "!flex !items-start")}>
          <div className={cx("section-item")}>
            <p className={cx("section-title")}>Primary Billing Address</p>
            <p className={cx("text-white")}>
              {props.invoice?.customer.name}
            </p>
            <p className={cx("text-white")}>
              {props.invoice?.customer.address}
            </p>
            {/* <p className={cx("text-red")}>
              Insurance Co.: {props.invoice?.insuranceCompany.name}
            </p>
            <p className={cx("text-red")}>
              Claim: {props.invoice?.insuranceCompany.number}
            </p>
            <p className={cx("text-red", "!font-[400]")}>
              Email: {props.invoice?.insuranceCompany.email}
            </p> */}

            {props.invoice?.secondaryBillingAddress && (
              <>
                <p className={cx("section-title")}>Secondary Billing Address</p>
                <p className={cx("text-white")}>
                  {props.invoice.secondaryBillingAddress.name}
                </p>
                <p className={cx("text-white")}>
                  {props.invoice.secondaryBillingAddress.address}
                </p>
              </>
            )}
          </div>

          <div className={cx("table-wrapper")}>
            <div className={cx("table-information")}>
              {tableInformation.map((item) => (
                <div className={cx("table-information-row")}>
                  <div className={cx("table-information-label")}>
                    {item.label}
                  </div>
                  <div className={cx("table-information-value")}>
                    {item.value}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className={cx("sections-container")}>
        <div className={cx("table-wrapper")}>
          <table className={cx("sections-table")}>
            <thead>
              <tr>
                <th className={cx("col-item")}>
                  {props.invoice?.sectionsTable?.[0]?.title || "Item"}
                </th>
                <th className={cx("col-quantity")}>Quantity</th>
                <th className={cx("col-price")}>Price</th>
                <th className={cx("col-amount")}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {props.invoice?.sectionsTable?.map((section, sectionIndex) => (
                <React.Fragment key={sectionIndex}>
                  {sectionIndex > 0 && section.title && (
                    <tr className={cx("section-header-row")}>
                      <td colSpan={4} className={cx("section-header")}>
                        {section.title}
                      </td>
                    </tr>
                  )}
                  {section.lines.map((line, lineIndex) => (
                    <tr key={lineIndex} className={cx("item-row")}>
                      <td className={cx("col-item")}>{line.name}</td>
                      <td className={cx("col-quantity")}>
                        {line.quantity.toFixed(2)}
                      </td>
                      <td className={cx("col-price")}>
                        $
                        {line.price.toLocaleString("en-US", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </td>
                      <td className={cx("col-amount")}>
                        $
                        {line.amount.toLocaleString("en-US", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {hasInvoicePayments && (
        <div className={cx("payments-container")}>
          <div className={cx("table-wrapper")}>
            <table className={cx("payments-table")}>
              <thead>
                <tr>
                  <th className={cx("col-payment-date")}>Payment Date</th>
                  <th className={cx("col-reference")}>Reference #</th>
                  <th className={cx("col-method")}>Method</th>
                  <th className={cx("col-amount")}>Amount</th>
                  <th className={cx("col-status")}>Status</th>
                </tr>
              </thead>
              <tbody>
                {invoicePayments.map((payment, paymentIndex) => (
                  <tr key={`${payment.reference}-${paymentIndex}`} className={cx("item-row")}>
                    <td className={cx("col-payment-date")}>
                      {payment.paymentDate
                        ? dayjs(payment.paymentDate).format("MM/DD/YYYY")
                        : "-"}
                    </td>
                    <td className={cx("col-reference")}>{payment.reference || "-"}</td>
                    <td className={cx("col-method")}>{payment.method || "-"}</td>
                    <td className={cx("col-amount")}>$
                      {formatCurrency(payment.amount || "0")}
                    </td>
                    <td className={cx("col-status")}>{payment.status || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className={cx("information-container", "!bg-transparent")}>
        <div className={cx("information-row", "!flex !items-start")}>
          <div className={cx("note-container")}>
            <p className={cx("note-text")}>{props.invoice?.note}</p>
          </div>

          <div className={cx("table-wrapper")}>
            <div className={cx("table-information")}>
              {subTotalTableInformation.map((item) => (
                <div className={cx("table-information-row")}>
                  <div className={cx("table-information-label")}>
                    {item.label}
                  </div>
                  <div className={cx("table-information-value")}>
                    {item.value}
                  </div>
                </div>
              ))}
            </div>

            <p className={cx("note-text")}>
              *Card payment may incur a surcharge which is calculated at
              checkout.
            </p>

            <div className={cx("make-payment-container")}>
              <PrimaryButton
                title={canPay ? "Make a payment" : "Paid in full"}
                onClick={props.onMakePayment}
                disabled={!canPay}
                className={cx("make-payment-button")}
              />

              <div className={cx("lock")}>
                <LockIcon className={cx("lock-icon")} />
                <p className={cx("lock-text")}>Verified secure</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoiceDetails;
